import { Suspense } from 'react';

import GlobalStyles from '@mui/material/GlobalStyles';
import { StyledEngineProvider } from '@mui/material/styles';
import {
  loginByFacebook,
  loginByGoogle,
  parseJwt,
  switchTenant,
} from '@realadvisor/auth-client';
import { init } from '@sentry/react';
import posthog from 'posthog-js';
import { createRoot } from 'react-dom/client';

import '../tailwind.css';

import { ApolloHasuraWrapper } from '@graphql/ApolloHasuraWrapper';
import { RelayApiWrapper } from '@graphql/RelayApiWrapper';
import { AppDataProvider } from '@modules/_shared/providers/AppDataProvider';
import { AnalyticsDebugProvider, AnalyticsProvider } from '@shared/analytics';
import {
  POSTHOG_API_KEY,
  POSTHOG_HOST,
  POSTHOG_UI_HOST,
  SENTRY_DSN,
  isProduction,
} from '@shared/config';
import { nylasLoginReturnRoute } from '@shared/email/emailClientConnect';

import { App } from './App';
import { scrubSentryBreadcrumb, scrubSentryEvent } from './sentryScrub';

if (typeof window !== 'undefined' && POSTHOG_API_KEY) {
  try {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST,
      defaults: '2026-01-30',
      person_profiles: 'identified_only',
      capture_pageview: false, // We'll handle this manually to have more control
      autocapture: false,
      // Session recording - disabled by default, started manually for specific flows
      disable_session_recording: true,
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: {
          password: true,
        },
      },
    });
  } catch (error) {
    console.warn('Failed to initialize PostHog:', error);
  }
}

const getRedirectBackUri = () => {
  const { searchParams, hostname } = new URL(location.href);
  const redirect_uri = searchParams.get('redirect_uri');
  if (redirect_uri == null) {
    throw Error('Invalid redirect uri');
  }

  //check that redirect URI is on the same tenant than current url otherwise modify it
  const redirectURL = new URL(redirect_uri);
  if (hostname !== redirectURL.hostname) {
    redirectURL.hostname = hostname;
  }

  if (redirectURL.pathname.includes('/noaccess')) {
    redirectURL.pathname = '/';
  }

  const access_token = localStorage.getItem('access_token');
  if (access_token != null) {
    const { user } = parseJwt(access_token);
    if (user != null) {
      return redirectURL.href;
    }
  }

  const select_tenant_url = new URL(`${location.origin}/en/select-tenant`);
  select_tenant_url.searchParams.set('return', redirect_uri);
  return select_tenant_url.toString();
};

const getErrorRedirectUri = (error: string) => {
  const { searchParams } = new URL(location.href);
  const redirect_uri = searchParams.get('redirect_uri');
  if (redirect_uri == null) {
    throw Error('Invalid redirect uri');
  }
  const login_url = new URL(`${location.origin}/login`);
  // prevent showing error when user is logged out
  if (error === 'LOGIN_NOT_FOUND') {
    // and redirect back after login
    login_url.searchParams.set('return', location.href);
  } else {
    login_url.searchParams.set('error', error);
    login_url.searchParams.set('return', redirect_uri);
  }
  return login_url.toString();
};

// Enabled only in production.
import.meta.env.NODE_ENV === 'production' &&
  isProduction &&
  init({
    dsn: SENTRY_DSN,
    sampleRate: 1.0, //  Capture 100% of the errors we can tweak this later
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions we can tweak this later
    environment: import.meta.env.NODE_ENV,
    release: import.meta.env.SENTRY_RELEASE,
    beforeSend: scrubSentryEvent,
    beforeSendTransaction: scrubSentryEvent,
    beforeBreadcrumb: scrubSentryBreadcrumb,
  });

const url = new URL(location.href);
const authCode = url.searchParams.get('auth_code');
const tenantId = url.searchParams.get('tenant_id');
const refreshToken = localStorage.getItem('refresh_token');

if (location.pathname === '/login/google') {
  (async () => {
    const response = await loginByGoogle();
    if (response.status >= 400) {
      const data = await response.json();
      location.replace(getErrorRedirectUri(data.error));
    } else {
      location.replace(getRedirectBackUri());
    }
  })();
} else if (location.pathname === '/login/facebook') {
  (async () => {
    const response = await loginByFacebook();
    if (response.status >= 400) {
      const data = await response.json();
      location.replace(getErrorRedirectUri(data.error));
    } else {
      location.replace(getRedirectBackUri());
    }
  })();
} else {
  (async () => {
    try {
      if (authCode != null && tenantId != null && refreshToken == null) {
        localStorage.setItem('refresh_token', authCode);
        await switchTenant();
      }
    } finally {
      // Used once or ignored, never kept: a reload on a URL still carrying
      // someone's code would sign the tab into that session.
      if (authCode != null) {
        url.searchParams.delete('auth_code');
        url.searchParams.delete('tenant_id');
        window.history.replaceState(window.history.state, '', url.toString());
      }
    }
    const app = (
      <StyledEngineProvider enableCssLayer>
        <GlobalStyles styles="@layer theme, base, mui, components, utilities;" />
        <AnalyticsDebugProvider>
          <AnalyticsProvider posthogClient={posthog}>
            <ApolloHasuraWrapper>
              <RelayApiWrapper>
                <Suspense fallback={null}>
                  <AppDataProvider>
                    <App />
                  </AppDataProvider>
                </Suspense>
              </RelayApiWrapper>
            </ApolloHasuraWrapper>
          </AnalyticsProvider>
        </AnalyticsDebugProvider>
      </StyledEngineProvider>
    );

    const mountNode = document.getElementById('root');

    if (mountNode != null) {
      const routes = [nylasLoginReturnRoute({ mountNode })];
      if (routes.includes(true) === false) {
        const root = createRoot(mountNode);
        // nothing matches
        root.render(app);
      }
    }
  })();
}
